'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { SearchBar } from './SearchBar';
import { IMG } from '../../imgPrefix';

interface EventsHeroProps {
  breadcrumbs?: { label: string; href?: string }[];
  title?: string;
  /** Path to the banner background image (e.g. /image/city/london-big.jpg) */
  bannerImage?: string;
  /** Fallback image if bannerImage fails to load */
  bannerFallback?: string;
  /** Pre-fill search bar with this value */
  searchValue?: string;
}

const defaultBreadcrumbs = [
  { label: 'Home', href: '/' },
  { label: 'Sports', href: '/sports' },
];

const DEFAULT_BANNER = `${IMG}/image/football-img.png`;

export function EventsHero({
  breadcrumbs = defaultBreadcrumbs,
  title = 'Sports',
  bannerImage,
  bannerFallback,
  searchValue,
}: EventsHeroProps) {
  const [bgImage, setBgImage] = useState(bannerImage || DEFAULT_BANNER);
  const fallback = bannerFallback || DEFAULT_BANNER;

  // Preload image to detect errors (since CSS background-image has no onError)
  useEffect(() => {
    if (!bannerImage || bannerImage === DEFAULT_BANNER) return;
    const img = new window.Image();
    img.src = bannerImage;
    img.onerror = () => setBgImage(fallback);
  }, [bannerImage, fallback]);

  return (
    <>
      <section>
        <div className="container">
          <div className="row">
            <div className="col">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                      <li
                        key={index}
                        className={`breadcrumb-item${isLast ? ' active' : ''}`}
                        {...(isLast ? { 'aria-current': 'page' as const } : {})}
                      >
                        {isLast || !crumb.href ? (
                          crumb.label
                        ) : (
                          <Link href={crumb.href}>{crumb.label}</Link>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <section className="hero-3 Component-Widget section-padding" data-highlightable="0">
        <div className="container">
          <div className="hero-3-sports" style={{ backgroundImage: `url('${bgImage}')` }}>
            <div className="hero-sports-inner">
              <h1 className="text-white text-center mb-4">{title}</h1>
              <div className="hero-search-wrap hero-form">
                <SearchBar initialSearch={searchValue} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
